import axios from 'axios';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'https://api.ruyan.app/api';

const api = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Request interceptor to add auth token
api.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('admin_token');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Response interceptor to handle auth errors
api.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      localStorage.removeItem('admin_token');
      window.location.href = '/login';
    }
    return Promise.reject(error);
  }
);

export interface LoginResponse {
  success: boolean;
  token?: string;
  user?: {
    id: string;
    email: string;
    name: string;
    role: string;
  };
  message?: string;
}

export interface User {
  id: string;
  email: string;
  name: string;
  role: string;
}

export const authService = {
  async login(email: string, password: string): Promise<LoginResponse> {
    try {
      const response = await api.post('/admin/login', { email, password });
      return {
        success: true,
        token: response.data.token,
        user: response.data.user
      };
    } catch (error: any) {
      return {
        success: false,
        message: error.response?.data?.message || 'Giriş başarısız'
      };
    }
  },

  async getProfile(): Promise<User> {
    const response = await api.get('/admin/profile');
    return response.data.user;
  },

  async logout(): Promise<void> {
    try {
      await api.post('/admin/logout');
    } catch (error) {
      console.error('Logout error:', error);
    }
  }
};

export const adminService = {
  async getStats() {
    const response = await api.get('/admin/stats');
    return response.data;
  },

  async getUsers(page = 1, limit = 50, search = '', filters = {}, sortBy = 'createdAt', sortOrder = 'desc') {
    const response = await api.get('/admin/users', {
      params: { 
        page, 
        limit, 
        search,
        ...filters,
        sortBy,
        sortOrder
      }
    });
    return response.data;
  },

  async getUserById(id: string) {
    const response = await api.get(`/admin/users/${id}`);
    return response.data;
  },

  async createUser(userData: {
    email: string;
    password: string;
    name?: string;
    role?: string;
    isPremium?: boolean;
  }) {
    const response = await api.post('/admin/users', userData);
    return response.data;
  },

  async updateUser(id: string, userData: {
    email?: string;
    name?: string;
    role?: string;
    isPremium?: boolean;
    isEmailVerified?: boolean;
  }) {
    const response = await api.put(`/admin/users/${id}`, userData);
    return response.data;
  },

  async deleteUser(id: string) {
    const response = await api.delete(`/admin/users/${id}`);
    return response.data;
  },

  async addCredits(userId: string, amount: number, reason?: string) {
    const response = await api.post(`/admin/users/${userId}/credits`, { credits: amount, reason });
    return response.data;
  },

  async updateSubscription(userId: string, status: 'PREMIUM' | 'FREE', durationMonths?: number) {
    const isPremium = status === 'PREMIUM';
    const payload: any = { isPremium };

    if (isPremium && durationMonths) {
      const endDate = new Date();
      endDate.setMonth(endDate.getMonth() + durationMonths);
      payload.subscriptionEndDate = endDate.toISOString();
      payload.subscriptionPlanId = durationMonths === 1 ? 'monthly' : 'yearly';
    }

    const response = await api.post(`/admin/users/${userId}/subscription`, payload);
    return response.data;
  },

  async getDreams(page = 1, limit = 50, search = '') {
    const response = await api.get('/admin/dreams', {
      params: { page, limit, search }
    });
    return response.data;
  },

  async getCosts(period = 30) {
    const response = await api.get('/admin/costs', {
      params: { period }
    });
    return response.data;
  },

  async getAzureCredit() {
    const response = await api.get('/admin/azure-credit');
    return response.data as {
      ok: boolean;
      provider: string;
      balance: number;
      currency: string;
      expiry: string | null;
      daysUntilExpiry: number | null;
      dailyBurn: number;
      projectedSpend: number | null;
      utilization: number | null;
      daysUntilDepleted: number | null;
      status: 'surplus' | 'tight' | 'depleting' | 'unknown';
      recommendation: string | null;
      lastUpdated: string;
    };
  },

  async getApiHealth() {
    const response = await api.get('/admin/api-health');
    return response.data;
  },

  async getLocalizationIssues(params: {
    language?: string;
    platform?: string;
    limit?: number;
    includeTranslated?: boolean;
  } = {}) {
    const response = await api.get('/admin/localizations/issues', {
      params,
    });
    return response.data;
  },

  async saveLocalizationTranslation(payload: {
    key: string;
    language: string;
    text: string;
    status?: string;
    source?: string;
  }) {
    const response = await api.post('/admin/localizations/translations', payload);
    return response.data;
  },

  async autoTranslateLocalizations(payload: { language: string; limit?: number }) {
    const response = await api.post('/admin/localizations/auto-translate', payload);
    return response.data;
  },

  async getUserActivities(userId: string, page = 1, limit = 50) {
    const response = await api.get(`/admin/users/${userId}/activities`, {
      params: { page, limit }
    });
    return response.data;
  },

  async getUserStatistics(userId: string, period = 30) {
    const response = await api.get(`/admin/users/${userId}/statistics`, {
      params: { period }
    });
    return response.data;
  },

  async getUserNotifications(userId: string, page = 1, limit = 50, filters: any = {}) {
    const response = await api.get(`/admin/users/${userId}/notifications`, {
      params: { page, limit, ...filters }
    });
    return response.data;
  },

  async createUserNotification(userId: string, notificationData: {
    title: string;
    message: string;
    type?: string;
    category?: string;
    isImportant?: boolean;
    expiresAt?: string;
  }) {
    const response = await api.post(`/admin/users/${userId}/notifications`, notificationData);
    return response.data;
  },

  async getUserBackups(userId: string, page = 1, limit = 20) {
    const response = await api.get(`/admin/users/${userId}/backups`, {
      params: { page, limit }
    });
    return response.data;
  },

  async createUserBackup(userId: string, backupData: {
    backupType?: string;
    description?: string;
  }) {
    const response = await api.post(`/admin/users/${userId}/backups`, backupData);
    return response.data;
  },

  async getUserAuditLogs(userId: string, page = 1, limit = 50, filters: any = {}) {
    const response = await api.get(`/admin/users/${userId}/audit-logs`, {
      params: { page, limit, ...filters }
    });
    return response.data;
  },

  async changeUserPassword(userId: string, passwordData: {
    newPassword: string;
    currentPassword?: string;
  }) {
    const response = await api.put(`/admin/users/${userId}/password`, passwordData);
    return response.data;
  },

  async getUserSecuritySettings(userId: string) {
    const response = await api.get(`/admin/users/${userId}/security`);
    return response.data;
  },

  async updateUserSecuritySettings(userId: string, securityData: {
    twoFactorEnabled?: boolean;
    loginNotifications?: boolean;
    suspiciousActivityAlerts?: boolean;
    securityQuestions?: any[];
  }) {
    const response = await api.put(`/admin/users/${userId}/security`, securityData);
    return response.data;
  },

  async getUserPreferences(userId: string) {
    const response = await api.get(`/admin/users/${userId}/preferences`);
    return response.data;
  },

  async updateUserPreferences(userId: string, preferencesData: {
    language?: string;
    theme?: string;
    notifications?: boolean;
    emailNotifications?: boolean;
    pushNotifications?: boolean;
    dreamReminders?: boolean;
    weeklyReports?: boolean;
    interpretationMethod?: string;
    privacyLevel?: string;
    dataSharing?: boolean;
  }) {
    const response = await api.put(`/admin/users/${userId}/preferences`, preferencesData);
    return response.data;
  },

  async resetUserPreferences(userId: string) {
    const response = await api.post(`/admin/users/${userId}/preferences/reset`);
    return response.data;
  },

  async getUserSettings(userId: string) {
    const response = await api.get(`/admin/users/${userId}/settings`);
    return response.data;
  },

  async updateUserSettings(userId: string, settingsData: {
    appVersion?: string;
    autoUpdate?: boolean;
    betaFeatures?: boolean;
    cacheEnabled?: boolean;
    imageQuality?: string;
    animationSpeed?: string;
    dataSync?: boolean;
    dailyLimit?: number;
    reminderTime?: string;
    weekendMode?: boolean;
    offlineMode?: boolean;
    debugMode?: boolean;
    analyticsEnabled?: boolean;
    crashReporting?: boolean;
    telemetryEnabled?: boolean;
    timezone?: string;
    dateFormat?: string;
    timeFormat?: string;
    currency?: string;
  }) {
    const response = await api.put(`/admin/users/${userId}/settings`, settingsData);
    return response.data;
  },

  async resetUserSettings(userId: string) {
    const response = await api.post(`/admin/users/${userId}/settings/reset`);
    return response.data;
  },

  async getUserThemeSettings(userId: string) {
    const response = await api.get(`/admin/users/${userId}/theme`);
    return response.data;
  },

  async updateUserThemeSettings(userId: string, themeData: {
    primaryTheme?: string;
    accentColor?: string;
    secondaryColor?: string;
    backgroundColor?: string;
    surfaceColor?: string;
    textColor?: string;
    borderColor?: string;
    fontSize?: string;
    fontFamily?: string;
    lineHeight?: string;
    borderRadius?: string;
    spacing?: string;
    sidebarWidth?: string;
    headerHeight?: string;
    cardStyle?: string;
    listStyle?: string;
    animationEnabled?: boolean;
    animationSpeed?: string;
    transitionDuration?: number;
    hoverEffects?: boolean;
    loadingAnimations?: boolean;
    customCSS?: string;
    customColors?: any;
    themePreset?: string;
    mobileLayout?: string;
    tabletLayout?: string;
    desktopLayout?: string;
    highContrast?: boolean;
    reducedMotion?: boolean;
    focusIndicators?: boolean;
    colorBlindSupport?: boolean;
  }) {
    const response = await api.put(`/admin/users/${userId}/theme`, themeData);
    return response.data;
  },

  async resetUserThemeSettings(userId: string) {
    const response = await api.post(`/admin/users/${userId}/theme/reset`);
    return response.data;
  },

  // Audit Logs
  async getAuditLogs(params: {
    page?: number;
    limit?: number;
    actorUserId?: string;
    targetUserId?: string;
    action?: string;
    severity?: string;
    entityType?: string;
    from?: string;
    to?: string;
  } = {}) {
    const response = await api.get('/admin/audit-logs', {
      params: {
        page: params.page || 1,
        limit: params.limit || 50,
        ...params
      }
    });
    return response.data;
  },

  async getAuditLogById(id: string) {
    const response = await api.get(`/admin/audit-logs/${id}`);
    return response.data;
  }
};

export const monitoringService = {
  async getHealthStatus() {
    const response = await axios.get('https://api.ruyan.app/api/monitoring/health');
    return response.data;
  },

  async getPerformanceMetrics(timeWindow = 60) {
    const response = await api.get('/monitoring/metrics', {
      params: { timeWindow }
    });
    return response.data;
  },

  async getDetailedPerformance(timeWindow = 60, type = 'all') {
    const response = await api.get('/monitoring/performance', {
      params: { timeWindow, type }
    });
    return response.data;
  },

  async resetMetrics() {
    const response = await api.post('/monitoring/reset');
    return response.data;
  }
};
